import type { PopupRequest } from "@azure/msal-browser"
import { BrowserAuthError } from "@azure/msal-browser"
import { Button, Modal } from "@relex-solutions/fragments"
import { createContext, type PropsWithChildren, useCallback, useContext, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AUTH_METHOD } from "@/shared/constants/authMethods"
import { logger } from "@/shared/utils/add/logger/logger"
import { AuthContext } from "./AuthProvider"
import styles from "./SessionRecoveryProvider.module.css"

type SessionRecoveryContext = {
  requestSessionRecovery: () => Promise<void>
}

export const SessionRecoveryContext = createContext<SessionRecoveryContext | null>(null)

export function useSessionRecovery() {
  const context = useContext(SessionRecoveryContext)
  if (!context) {
    throw new Error("useSessionRecovery must be used within SessionRecoveryProvider")
  }
  return context
}

type PendingRecovery = {
  resolve: () => void
  reject: (error: Error) => void
}

export function SessionRecoveryProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation()
  const auth = useContext(AuthContext)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const pendingRecoveriesRef = useRef<PendingRecovery[]>([])

  const requestSessionRecovery = useCallback((): Promise<void> => {
    if (auth?.type !== AUTH_METHOD.MSAL) {
      return Promise.reject(new Error("Session recovery only available for MSAL auth"))
    }

    return new Promise((resolve, reject) => {
      pendingRecoveriesRef.current.push({ resolve, reject })
      setIsOpen(true)
    })
  }, [auth])

  const handleResume = useCallback(async () => {
    if (auth?.type !== AUTH_METHOD.MSAL) return

    setIsLoading(true)
    try {
      const request: PopupRequest = { scopes: auth.scopes }
      await auth.acquireTokenInteractive(request)

      for (const pending of pendingRecoveriesRef.current) {
        pending.resolve()
      }
      pendingRecoveriesRef.current = []
      setIsOpen(false)
    } catch (error) {
      if (error instanceof BrowserAuthError && error.errorCode === "user_cancelled") {
        logger.debug("User cancelled session recovery popup")
        return
      }

      const err = error instanceof Error ? error : new Error("Session recovery failed", { cause: error })
      logger.error("Session recovery failed:", err)
      for (const pending of pendingRecoveriesRef.current) {
        pending.reject(err)
      }
      pendingRecoveriesRef.current = []
      setIsOpen(false)
    } finally {
      setIsLoading(false)
    }
  }, [auth])

  const handleClose = useCallback(() => {
    const err = new Error("Session recovery cancelled by user")
    for (const pending of pendingRecoveriesRef.current) {
      pending.reject(err)
    }
    pendingRecoveriesRef.current = []
    setIsOpen(false)
  }, [])

  const contextValue: SessionRecoveryContext = { requestSessionRecovery }

  return (
    <SessionRecoveryContext.Provider value={contextValue}>
      {children}
      <Modal
        open={isOpen}
        title={t("sessionRecovery.title")}
        onClose={handleClose}
        hasCloseIcon
        maxWidth="small"
        footer={
          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              {t("general.cancel")}
            </Button>
            <Button variant="primary" onClick={handleResume} disabled={isLoading}>
              {isLoading ? t("sessionRecovery.resuming") : t("sessionRecovery.resume")}
            </Button>
          </div>
        }
      >
        <p className={styles.message}>{t("sessionRecovery.message")}</p>
      </Modal>
    </SessionRecoveryContext.Provider>
  )
}
